import { keywords } from "../keywords.js";
import { TestParserOptions } from "../../options/testParserOptions.js";

/**
 * Generates a test title by replacing placeholders with test case values.
 * - `$i`: Replaced with the 1-based index of the test case.
 * - `$1`, `$2`, etc.: Replaced with the value of the corresponding argument.
 * 
 * @param title - The template title.
 * @param testCases - The list of arguments for each test case.
 * @param caseIndex - The index of the current test case.
 * @returns The generated test title.
 */
export function simpleReplace(title: string, testCases: any[], caseIndex: number): string {
  let newTitle = title.replaceAll("$i", (caseIndex + 1).toString());
  testCases.forEach(
    (arg, index) => {
      newTitle = newTitle.replaceAll(`$${index + 1}`, `${arg}`)
    }
  );
  return newTitle;
}

/**
 * Checks if a function has associated test cases defined via property or metadata.
 * 
 * @param value - The function to check.
 * @param options - Parser options used to check for literal 'testCases' properties.
 * @returns True if the function has test cases.
 */
export function hasPureFunctionCases(value: any, options: TestParserOptions): boolean {
  return Object.hasOwn(value, keywords.cases)
    || (options.parserFlags!.allowLiteralCases && Object.hasOwn(value, "testCases"));
}

/**
 * Extracts test cases from a function that uses an array-based test case definition.
 * 
 * @param value - The function-with-cases array.
 * @returns An array of test case arguments.
 */
export function getFunctionTestCases(value: any[]): any[][] {
  const testCases = value.slice(0, value.length - 1);
  return mapTestCases(testCases);
}

/**
 * Extracts test cases from a function's metadata or literal property.
 * 
 * @param value - The function to extract cases from.
 * @param options - Parser options.
 * @returns An array of test case arguments.
 */
export function getPureFunctionCases(value: any, options: TestParserOptions): any[][] {
  const testCases = value[keywords.cases]
    || (options.parserFlags!.allowLiteralCases && value["testCases"]);

  return mapTestCases(testCases);
}

/**
 * Normalizes test cases into an array of argument arrays.
 * 
 * @param testCases - Raw test case data.
 * @returns A normalized array of argument arrays.
 */
export function mapTestCases(testCases: any[]): any[][] {
  return testCases.map(x => {
    // check if we have an array already
    if (x instanceof Array) return x;

    // return single cases as an array
    return [x];
  });
}
